"use client";

import { WelcomeSteps } from "@/components/Welcome";
import { ServerContext } from "@/context/Server";
import useEventListener from "@/hooks/useEventListener";
import { useApp, useAppStore } from "@/store/app";
import useFirebaseStore from "@/store/firebase";
import {
  useReplicache,
  useReplicacheSongs,
  useReplicacheStore,
} from "@/store/replicache";
import demoSongs from "@/utils/demoSongs";
import fetchSongByID from "@/utils/fetchSongById";
import storage from "@/utils/storage";
import type {
  BeforeInstallPromptEvent,
  SetListId,
  SongId,
  Songs,
  User,
} from "@/utils/types";
import va from "@vercel/analytics";
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { isEmpty, isEqual } from "lodash";
import { useParams } from "next/navigation";
import { type ReactNode, useContext, useEffect, useState } from "react";
// @ts-ignore
import subscribe from "subscribe-event";
import { useDebouncedCallback } from "use-debounce";
import shallow from "zustand/shallow";

const LOG_TAG = "🍾 App";

if (typeof window !== "undefined") {
  try {
    // @ts-ignore
    window.analyticsLogEvent = (
      event: string,
      properties: Record<string, string | number | boolean | null> = {},
    ) => {
      //console.debug("analyticsLogEvent", { event, properties });
      va.track(event, properties);
    };
  } catch (err) {
    console.error(LOG_TAG, "analyticsLogEvent registration fail", err);
  }
}

export default function App({ children }: { children: ReactNode }) {
  useReplicache();
  const { session } = useContext(ServerContext);
  const { signOut, songAdd } = useApp();
  const { songs: replicacheSongs } = useReplicacheSongs();
  const { songId, setListId } = useParams() as {
    songId: SongId;
    setListId: SetListId;
  };
  const [songsFromUrl, setSongsFromUrl] = useState<Songs>({});

  const {
    firebaseAuth,
    user: firebaseUser,
    userModify: firebaseUserModify,
  } = useFirebaseStore(
    ({ firebaseAuth, user, userModify }) => ({
      firebaseAuth,
      user,
      userModify,
    }),
    shallow,
  );

  const { isFirstTimeUser, songIdMap } = useReplicacheStore(
    ({ isFirstTimeUser, songIdMap }) => ({
      isFirstTimeUser,
      songIdMap,
    }),
    shallow,
  );

  const {
    initializedState,
    setInitializedState,
    initializedStateFromStorage,
    user,
    selectedTab,
    setSelectedTab,
    setUser,
    setWindowSize,
    setOnLine,
    songs,
    waitingOnAuth,
    welcomeStep,
  } = useAppStore(
    ({
      initializedState,
      setInitializedState,
      initializedStateFromStorage,
      user,
      selectedTab,
      setSelectedTab,
      setUser,
      setWindowSize,
      setOnLine,
      songs,
      waitingOnAuth,
      welcomeStep,
    }) => ({
      initializedState,
      setInitializedState,
      initializedStateFromStorage,
      user,
      selectedTab,
      setSelectedTab,
      setUser,
      setWindowSize,
      setOnLine,
      songs,
      waitingOnAuth,
      welcomeStep,
    }),
  );

  useEffect(() => {
    const combinedSongs = { ...replicacheSongs, ...songsFromUrl };
    //console.debug(LOG_TAG, "useEffect combinedSongs", { combinedSongs });
    useAppStore.setState({ songs: combinedSongs });
  }, [replicacheSongs, songsFromUrl]);

  // These are some zustand store setups - we have to be careful with
  // doing these because it can lead to an infinite re-render situation.
  if (welcomeStep === -1) {
    useAppStore.setState({
      welcomeStep: session ? WelcomeSteps.AddASong : WelcomeSteps.FirstWelcome,
    });
  }

  useEffect(() => {
    if (!songId) return;
    if (songs[songId]) return;
    if (demoSongs.songs[songId]) return;
    const fn = async () => {
      console.debug(LOG_TAG, "useEffect songId URL param", { songId });
      try {
        const song = await fetchSongByID(songId);
        setSongsFromUrl({ [songId]: song });
      } catch (error) {
        console.error(LOG_TAG, "Error fetching song", { error });
      }
    };
    fn();
  }, [songs, songId]);

  useEffect(() => {
    if (waitingOnAuth) return;
    if (!session) return;
    const firebaseUser = firebaseAuth.currentUser;
    //console.debug(LOG_TAG, "useEffect session", { firebaseUser, session });
    const fn = async () => {
      const credential = GoogleAuthProvider.credential(session.user.id_token);
      console.debug(LOG_TAG, "useEffect session credential", {
        credential,
        firebaseAuth,
      });
      try {
        const result = await signInWithCredential(firebaseAuth, credential);
        console.debug(
          LOG_TAG,
          "useEffect session signInWithCredential result",
          { result },
        );
      } catch (err) {
        // @ts-ignore
        if (err?.code === "auth/network-request-failed") {
          console.debug(
            LOG_TAG,
            "useEffect session Firebase auth/network-request-failed",
          );
          throw err;
        }
        console.error(LOG_TAG, "useEffect session signInWithCredential", {
          err,
        });
        //window.alert("Auth is stale, please sign in again.");
        signOut();
      }
    };
    if (!firebaseUser) {
      fn();
    }
  }, [firebaseAuth, signOut, session, waitingOnAuth]);

  useEffect(() => {
    if (setListId && selectedTab !== "songlist") {
      setSelectedTab("songlist");
    }
  }, [setSelectedTab, selectedTab, setListId]);

  useEffect(() => {
    if (initializedState) return;
    if (initializedStateFromStorage) {
      console.debug(
        LOG_TAG,
        "SET INITIALIZED STATE",
        JSON.stringify({
          initializedState: initializedState,
          initializedStateFromStorage: initializedStateFromStorage,
          setInitializedState: setInitializedState,
        }),
      );
      setInitializedState(true);
    }
  }, [initializedState, initializedStateFromStorage, setInitializedState]);

  useEffect(() => {
    if (isEmpty(firebaseUser)) return;
    // console.debug(LOG_TAG, "🔥 -> 💁 firebaseUser/User useEffect", {
    //   firebaseUser,
    //   user,
    // });
    const derivedUser: User = {
      id: firebaseUser.uid,
      displayName: firebaseUser.displayName || "",
      email: firebaseUser.email || "",
      photoUrl: firebaseUser.photoURL || "",
      phoneNumber: firebaseUser.phoneNumber || "",
      creation_time: firebaseUser.metadata.creationTime || "",
    };
    if (!isEqual(derivedUser, user)) {
      console.debug(LOG_TAG, "🔥 -> 💁 setUser()", {
        derivedUser,
        user: user,
      });
      setUser(derivedUser);
    } else {
      /*console.debug(LOG_TAG, "🔥 <-> 💁 user IN-SYNC", {
        f_user: firebaseUser,
        user,
      });*/
    }
  }, [firebaseUser, setUser, user]);

  useEffect(() => {
    if (!user) return;
    //console.debug(LOG_TAG, "💁 user useEffect", { user });
    storage.setItem("user", user);
    firebaseUserModify(user);
  }, [firebaseUserModify, user]);

  // Initializes with demo songs after first login
  useEffect(() => {
    if (!isFirstTimeUser) return;
    if (!initializedStateFromStorage) return;
    if (!user) return;
    if (!isEmpty(songIdMap)) return;
    console.debug(`${LOG_TAG} ---- ADDING DEMO SONGS FOR THE FIRST TIME ----`, {
      isFirstTimeUser,
      initializedStateFromStorage,
      user,
      songIdMap,
    });
    const keys = Object.keys(demoSongs.songs);
    const now = Date.now();
    const fn = async () => {
      for (const demoSongId of keys) {
        const demoSong = {
          ...demoSongs.songs[demoSongId],
          id: "",
          view_count: 0,
          deleted_timestamp: 0,
          created_timestamp: now,
          updated_timestamp: now,
          created_by_user_id: user.id,
          updated_by_user_id: user.id,
        };
        const newDemoSong = await songAdd(demoSong);
        console.debug(LOG_TAG, "🎉 added demo song", { newDemoSong });
      }
    };
    fn();
  }, [isFirstTimeUser, initializedStateFromStorage, songIdMap, user, songAdd]);

  useEffect(() => {
    if (window.location.search.includes("elsigh-test")) {
      throw new Error("elsigh test error");
    }
  }, []);

  let beforeInstallPromptEvent: BeforeInstallPromptEvent;
  const onBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
    //console.debug("App captured onBeforeInstallPrompt", e);
    window.analyticsLogEvent("Captured OnBeforeInstallPrompt");
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    beforeInstallPromptEvent = e;
  };
  useEventListener("beforeinstallprompt", onBeforeInstallPrompt);

  const onOnlineOffline = () => {
    setOnLine(window.navigator.onLine);
  };
  useEventListener("offline", onOnlineOffline);
  useEventListener("online", onOnlineOffline);

  const onResize = useDebouncedCallback(() => {
    console.debug("🍾 window.resize", window.innerWidth, window.innerHeight);
    setWindowSize({
      width: window.document.body.clientWidth,
      height: window.document.body.clientHeight,
    });
  }, 1000);

  useEffect(() => {
    const unsubscribe = subscribe(window, "resize", onResize);
    return () => {
      onResize.flush();
      unsubscribe();
    };
  }, [onResize]);

  const getDisplayMode = () => {
    let displayMode: string;
    if (
      // `standalone` is not defined on window.navigator type-wise
      // @ts-ignore
      window.navigator.standalone ||
      window.matchMedia("(display-mode: standalone)").matches
    ) {
      displayMode = "Standalone";
    } else {
      displayMode = "BrowserTab";
    }
    return displayMode;
  };
  const onWindowLoad = () => {
    const displayMode = getDisplayMode();
    //console.debug("App 🍾 window.load", displayMode);
    window.analyticsLogEvent(`${LOG_TAG} onWindowLoad`, { displayMode });
  };
  useEventListener("load", onWindowLoad);

  return <>{children}</>;
}
